import { Suspense, useEffect, useState } from 'react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as Sentry from '@sentry/react';
import orderby from 'lodash.orderby';
import moment from 'moment-timezone';
import Sidebar from '../components/sidebar';
import SplashScreen from '../components/splash-screen';
import TopBar from '../components/top-bar';
import paths from '../router/paths';
import { getFinancialContact } from '../services/api-helpers/participants';
import {
  getCategories,
  getNdisCatalogue,
  getProduct,
  getRego,
} from '../services/api-helpers/setting';
import { getUserInfo } from '../services/api-helpers/user';
import { getNotificationStats } from '../services/api-helpers/utility';
import Auth from '../services/auth';
import {
  APP_LOADING,
  SET_LOADING_STATE,
  TOGGLE_GLOBAL_TOAST,
  UPDATE_CATEGORIES,
  UPDATE_DEFAULT_CONTACT_LIST,
  UPDATE_FINANCIAL_CONTACT,
  UPDATE_NDIS_CATALOGUE,
  UPDATE_NOTIFICATION_STATS,
  UPDATE_ORG_ID,
  UPDATE_PRODUCT,
  UPDATE_REGO_LIST,
  UPDATE_USER_INFO,
} from '../store/actions/action-types';
import { useDispatchToProps, useStateToProps } from '../store/actions/actions';

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 44;

const Footer = styled('footer')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  textAlign: 'right',
  justifyContent: 'space-between',
  height: '40px',
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.hint,
  padding: '0 20px',
  fontSize: '12px',
  position: 'fixed',
  bottom: 0,
  right: 0,
  zIndex: theme.zIndex.drawer - 1,
  width: '100%',

  '& .footerLicense': {
    textTransform: 'capitalize',
  },
}));

function MainLayout({ children }) {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(true);
  const [configError, setConfigError] = useState(false);
  const [orgid, setOrgid] = useState(null);
  const [isUserInfoLoaded, setUserInfoLoaded] = useState(false);

  const { user, appLoading, loadingStates } = useStateToProps((state) => ({
    user: state.app.user,
    appLoading: state.global.appLoading,
    loadingStates: state.app.loadingStates,
  }));

  const toggleToast = useDispatchToProps(TOGGLE_GLOBAL_TOAST);
  const updateUserInfo = useDispatchToProps(UPDATE_USER_INFO);
  const updateAppLoading = useDispatchToProps(APP_LOADING);
  const updateOrgID = useDispatchToProps(UPDATE_ORG_ID);
  const updateNotificationStats = useDispatchToProps(UPDATE_NOTIFICATION_STATS);
  const updateRegoInStore = useDispatchToProps(UPDATE_REGO_LIST);
  const updateFinancialContact = useDispatchToProps(UPDATE_FINANCIAL_CONTACT);
  const updateProduct = useDispatchToProps(UPDATE_PRODUCT);
  const updateCategories = useDispatchToProps(UPDATE_CATEGORIES);
  const updateNdisCatalogue = useDispatchToProps(UPDATE_NDIS_CATALOGUE);
  const updateDefaultContactList = useDispatchToProps(UPDATE_DEFAULT_CONTACT_LIST);
  const setLoadingState = useDispatchToProps(SET_LOADING_STATE);

  const drawerWidthCurrent = drawerOpen ? expandedDrawerWidth : collapsedDrawerWidth;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleGetUserInfo = async () => {
    setConfigError(null);
    updateAppLoading(true);
    const userId = Auth().getUserId();
    try {
      const { body } = await getUserInfo(userId);
      const orgId = Auth().getOrgId();
      updateOrgID(orgId);
      Sentry.setUser({
        email: body?.data?.email,
        license: body?.data?.appMetaData?.license,
        orgId,
      });
      const { termsAndConditionsStatus } = body?.data;
      if (termsAndConditionsStatus === false) {
        history.push(paths.termsAndConditions);
      } else if (!orgId) {
        history.push(paths.welcome);
      } else {
        updateUserInfo({
          ...body.data,
          timezone: localStorage.getItem('userTimezone'),
        });
        setUserInfoLoaded(true);
        setOrgid(orgId);
      }
    } catch (e) {
      toggleToast({
        label: 'An error has occurred. Please try again.',
        visibility: true,
      });
      setConfigError(true);
    } finally {
      updateAppLoading(false);
    }
  };

  const handleGetNotificationStats = async (orgId) => {
    const userId = Auth().getUserId();
    // setLoadingState({ key: 'notificationStats', value: true });
    try {
      const { body, ok } = await getNotificationStats({ userId, orgId });
      if (ok && body?.data) {
        updateNotificationStats(body.data);
      }
    } catch (e) {
      console.log(e);
    } finally {
      // setLoadingState({ key: 'notificationStats', value: false });
    }
  };

  const fetchRego = async (orgId) => {
    // setLoadingState({ key: 'rego', value: true });
    try {
      const { ok, body } = await getRego({
        userId: Auth().getUserId(),
        orgId,
        parameters: {
          page: 1,
          pageSize: 1000,
        },
      });
      if (ok) {
        const { data } = body;
        updateRegoInStore(data);
        let showToast = false;
        data.map(({ deviceExpiry }) => {
          const isDVAExpired = deviceExpiry ? moment(deviceExpiry).isBefore(moment()) : false;
          const isDVAAboutToExpire = deviceExpiry
            ? moment(deviceExpiry).isBefore(moment().add(7, 'days')) && !isDVAExpired
            : false;
          if (isDVAExpired || isDVAAboutToExpire) {
            showToast = true;
          }
          return showToast;
        });
        if (showToast) {
          toggleToast({
            label:
              'One or more of your devices are expired or about to expire. Please visit the registration page to renew their expiry.',
            visibility: true,
            type: 'warning',
            timeout: 300000,
          });
        }
      }
    } catch (e) {
      console.log('An error has occurred in fetchRego', e);
    } finally {
      // setLoadingState({ key: 'rego', value: false });
    }
  };

  const fetchFinancialContact = async (orgId) => {
    // setLoadingState({ key: 'financialContact', value: true });
    try {
      const { ok, body } = await getFinancialContact({
        userId: Auth().getUserId(),
        orgId,
        parameters: {
          page: 1,
          pageSize: 1000,
        },
      });
      if (ok) {
        const sortedFinancialContact = orderby(body.data, [(u) => u.company?.toLowerCase()]);
        updateFinancialContact(sortedFinancialContact);
      } else {
        toggleToast({
          label: body.message || 'An error has occurred. Please try again.',
          visibility: true,
        });
      }
    } catch (e) {
      toggleToast({
        label: e.message,
        visibility: true,
      });
    } finally {
      // setLoadingState({ key: 'financialContact', value: false });
    }
  };

  const fetchCategoryX = async (orgId) => {
    // setLoadingState({ key: 'categoryX', value: true });
    try {
      const { ok, body } = await getCategories({
        userId: Auth().getUserId(),
        orgId,
        categoryType: 'x',
        parameters: {
          page: 1,
          pageSize: 1000,
        },
      });
      if (ok) {
        const sortedCategoryX = orderby(body.data, [(u) => u.categoryXCode?.toLowerCase()]);

        updateCategories({
          type: 'x',
          data: sortedCategoryX,
        });
      } else {
        toggleToast({
          label: body.message || 'An error has occurred. Please try again.',
          visibility: true,
        });
      }
    } catch (e) {
      toggleToast({
        label: e.message,
        visibility: true,
      });
    } finally {
      // setLoadingState({ key: 'categoryX', value: false });
    }
  };

  const fetchCategoryY = async (orgId) => {
    // setLoadingState({ key: 'categoryY', value: true });
    try {
      const { ok, body } = await getCategories({
        userId: Auth().getUserId(),
        orgId,
        categoryType: 'y',
        parameters: {
          page: 1,
          pageSize: 1000,
        },
      });
      if (ok) {
        const sortedCategoryY = orderby(body.data, [(u) => u.categoryYCode?.toLowerCase()]);
        updateCategories({
          type: 'y',
          data: sortedCategoryY,
        });
      } else {
        toggleToast({
          label: body.message || 'An error has occurred. Please try again.',
          visibility: true,
        });
      }
    } catch (e) {
      toggleToast({
        label: e.message,
        visibility: true,
      });
    } finally {
      // setLoadingState({ key: 'categoryY', value: false });
    }
  };

  const fetchProduct = async (orgId) => {
    // setLoadingState({ key: 'product', value: true });
    try {
      const { ok, body } = await getProduct({
        userId: Auth().getUserId(),
        orgId,
        parameters: {
          page: 1,
          pageSize: 1000,
        },
      });
      if (ok) {
        updateProduct(body.data);
      } else {
        toggleToast({
          label: body.message || 'An error has occurred. Please try again.',
          visibility: true,
        });
      }
    } catch (e) {
      toggleToast({
        label: e.message,
        visibility: true,
      });
    } finally {
      // setLoadingState({ key: 'product', value: false });
    }
  };

  const fetchNdisCatalogue = async () => {
    // setLoadingState({ key: 'ndisCatalogue', value: true });
    try {
      const { ok, body } = await getNdisCatalogue();
      if (ok) {
        updateNdisCatalogue(body.data);
      } else {
        toggleToast({
          label: body.message || 'An error has occurred. Please try again.',
          visibility: true,
        });
      }
    } catch (e) {
      toggleToast({
        label: e.message,
        visibility: true,
      });
    } finally {
      // setLoadingState({ key: 'ndisCatalogue', value: false });
    }
  };

  useEffect(() => {
    (async () => {
      const userTimezone = localStorage.getItem('userTimezone');
      if (userTimezone === null) {
        localStorage.setItem('userTimezone', moment.tz.guess());
      }
      await handleGetUserInfo();
    })();
  }, []);

  useEffect(() => {
    if (orgid) {
      (async () => {
        await handleGetNotificationStats(orgid);
        await fetchRego(orgid);
        await fetchCategoryY(orgid);
        await fetchCategoryX(orgid);
        await fetchFinancialContact(orgid);
        await fetchProduct(orgid);
        await fetchNdisCatalogue();
        updateDefaultContactList(['PLSL', 'Agency']);
      })();
    }
  }, [orgid]);

  const drawerTransitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidthCurrent}px)` },
          ml: { sm: `${drawerWidthCurrent}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: drawerTransitionDuration.enter,
          }),
        }}
      >
        <TopBar
          onToggleClick={handleDrawerToggle}
          isMobile={isMobile}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
        />
      </AppBar>

      {/* Navigation drawer - now using the refactored Sidebar component */}
      <Sidebar
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        handleDrawerToggle={handleDrawerToggle}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: '0',
          width: { xs: '100%', sm: `calc(100% - ${drawerWidthCurrent}px)` },
          minHeight: 'calc(100vh - 40px)',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: drawerTransitionDuration.enter,
          }),
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: '40px',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1 }}>
          {appLoading || configError || !isUserInfoLoaded ? (
            <Box justifyContent="center" alignItems="center" display="flex" height="100%">
              <SplashScreen onTryAgain={handleGetUserInfo} showTryAgain={Boolean(configError)} />
            </Box>
          ) : (
            <Suspense fallback={<SplashScreen />}>{children}</Suspense>
          )}
        </Box>
      </Box>
      <Footer
        sx={{
          width: { sm: `calc(100% - ${drawerWidthCurrent}px)` },
          ml: { sm: `${drawerWidthCurrent}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: drawerTransitionDuration.enter,
          }),
        }}
      >
        <span>
          <span>
            {user?.appMetaData?.license && (
              <span>
                License: <span className="footerLicense">{user?.appMetaData?.license}</span>
              </span>
            )}
          </span>
          <span style={{ padding: '0 20px' }}>|</span>
          Timezone: {user?.timezone}
        </span>
        <span>© 2025 - quickclaim</span>
      </Footer>
    </Box>
  );
}

export default MainLayout;
